/**
 * Application router — React Router v7, lazy route modules.
 *
 * Route groups mirror the legacy Next.js app-router structure; pages are
 * ported wave by wave (see MIGRATION_PARITY.md). Unported routes render a
 * placeholder pointing at the legacy app until parity.
 */

import { createBrowserRouter, Navigate } from 'react-router';

import { DashboardLayout } from '@/app/layouts/DashboardLayout';
import { RouteError } from '@/app/AppError';
import { AuthCallback, SilentRenew } from '@/routes/auth/callbacks';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { NotFoundPage } from '@/routes/NotFoundPage';
import { VersionsBuiltPage } from '@/routes/versions/VersionsBuiltPage';

/**
 * Full-screen splash rendered during the router's initial hydration, while the
 * first matched lazy route module resolves. Wired as the root route's
 * `HydrateFallback` so React Router has an element to render on initial load —
 * this silences the app-wide "No `HydrateFallback` element provided to render
 * during initial hydration" warning on every page. Token-themed (light + dark).
 */
function SplashScreen() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-bg text-foreground"
    >
      <span className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-primary" />
      <span className="text-sm text-muted">Chargement…</span>
      <span className="sr-only">Chargement de l’application</span>
    </div>
  );
}

export const router = createBrowserRouter([
  { path: '/auth/callback', element: <AuthCallback /> },
  { path: '/auth/silent-renew', element: <SilentRenew /> },
  // Public build-provenance page — no auth, so version parity can be checked
  // even when login is unavailable. See VersionsBuiltPage.
  { path: '/versionsbuilt', element: <VersionsBuiltPage /> },
  // Org-invite acceptance (collaboration, docs/COLLABORATION_CONTRACT.md §1) —
  // deliberately NOT under ProtectedRoute: an invitee may be account-less, and
  // the backend provisions their Keycloak account on accept. See
  // routes/team/AcceptOrgInvitePage.tsx for the full rationale.
  {
    path: '/accept-org-invite/:token',
    lazy: async () => {
      const { AcceptOrgInvitePage } = await import('@/routes/team/AcceptOrgInvitePage');
      return { Component: AcceptOrgInvitePage };
    },
  },
  // PUBLIC booking pages (Calendly-style) — an invitee has no account, so these
  // are deliberately NOT under ProtectedRoute. They call the public, token/slug-
  // authenticated crm-service endpoints (/api/v1/crm/book/:slug, /booking/:token).
  {
    path: '/book/:slug',
    lazy: async () => {
      const { PublicBookingPage } = await import('@/routes/booking/PublicBookingPage');
      return { Component: PublicBookingPage };
    },
  },
  {
    path: '/booking/:token',
    lazy: async () => {
      const { ManageBookingPage } = await import('@/routes/booking/ManageBookingPage');
      return { Component: ManageBookingPage };
    },
  },
  // PUBLIC web-form capture page — the `/f/{token}` embed link a CRM form hands
  // out. Deliberately NOT under ProtectedRoute: visitors are account-less and
  // the crm-service `/forms/{token}` endpoints are token-authenticated (public).
  {
    path: '/f/:token',
    lazy: async () => {
      const { PublicFormPage } = await import('@/routes/forms/PublicFormPage');
      return { Component: PublicFormPage };
    },
  },
  // PUBLIC e-signature page — the `/sign/{token}` link a sent CRM quote/proposal
  // hands out (crm.ts documentPublicUrl). Deliberately NOT under ProtectedRoute:
  // the signer is account-less; the crm-service `/sign/{token}` endpoints resolve
  // the tenant from the sign-token row (no JWT). See routes/sign/PublicSignPage.
  {
    path: '/sign/:token',
    lazy: async () => {
      const { PublicSignPage } = await import('@/routes/sign/PublicSignPage');
      return { Component: PublicSignPage };
    },
  },
  // PUBLIC link-in-bio page — the `/p/{slug}` link the bio builder hands out
  // (bio.ts publicUrl). Deliberately NOT under ProtectedRoute: visitors are
  // account-less; bio-service `/public/bio/{slug}` resolves the tenant from the
  // slug row (no JWT) and forwards captured leads via the bio's capture token.
  {
    path: '/p/:slug',
    lazy: async () => {
      const { PublicBioPage } = await import('@/routes/bio/PublicBioPage');
      return { Component: PublicBioPage };
    },
  },
  // PUBLIC CSAT survey response page — the `/csat/{token}` link a sent survey
  // hands out. Account-less; community-service `/webhooks/csat/{token}` resolves
  // the survey from the token row (no JWT) and records the score/comment.
  {
    path: '/csat/:token',
    lazy: async () => {
      const { PublicCsatPage } = await import('@/routes/community/PublicCsatPage');
      return { Component: PublicCsatPage };
    },
  },
  // PUBLIC privacy / confidentialité policy — deliberately NOT under
  // ProtectedRoute so a logged-out visitor (and links from the signup page, the
  // Keycloak login page, the app footer and the email CASL footer) can reach it.
  // Both slugs render the same self-contained FR/EN/AR page. Law-25/GDPR/CASL.
  {
    path: '/confidentialite',
    lazy: async () => {
      const { PrivacyPolicyPage } = await import('@/routes/legal/PrivacyPolicyPage');
      return { Component: PrivacyPolicyPage };
    },
  },
  {
    path: '/privacy',
    lazy: async () => {
      const { PrivacyPolicyPage } = await import('@/routes/legal/PrivacyPolicyPage');
      return { Component: PrivacyPolicyPage };
    },
  },
  {
    path: '/',
    // Root route carries the app-wide hydration fallback: every in-app page
    // matches under it, so React Router always finds a splash to render while
    // lazy route modules load — no more "No HydrateFallback element" warning.
    HydrateFallback: SplashScreen,
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    // Friendly full-page fallback for loader/lazy/render errors in the app tree.
    errorElement: <RouteError />,
    children: [
      {
        index: true,
        lazy: async () => {
          const { DashboardPage } = await import('@/routes/dashboard/DashboardPage');
          return { Component: DashboardPage };
        },
      },
      // Team HQ — "Centre de commande — Équipe 24/7": mission control for the
      // 24/7 auto-coordinated virtual team (master always-on switch, mandate,
      // coordinator cadence/budget, per-persona autonomy, live status).
      {
        path: 'ai-team/hq',
        lazy: async () => {
          const { TeamHQPage } = await import('@/routes/ai-team/TeamHQPage');
          return { Component: TeamHQPage };
        },
      },
      {
        path: 'ai-team/office',
        lazy: async () => {
          const { OfficePage } = await import('@/routes/ai-team/OfficePage');
          return { Component: OfficePage };
        },
      },
      {
        path: 'ai-team/teams',
        lazy: async () => {
          const { TeamsPage } = await import('@/routes/ai-team/TeamsPage');
          return { Component: TeamsPage };
        },
      },
      // User-configurable AI teams: seat-level builder (role × count, per-seat
      // skills/tools/autonomy), reusable role-slot templates, and the task pool.
      {
        path: 'ai-team/builder',
        lazy: async () => {
          const { TeamBuilderPage } = await import('@/routes/ai-team/TeamBuilderPage');
          return { Component: TeamBuilderPage };
        },
      },
      {
        path: 'ai-team/templates',
        lazy: async () => {
          const { TemplateGallery } = await import('@/routes/ai-team/TemplateGallery');
          return { Component: TemplateGallery };
        },
      },
      {
        path: 'ai-team/pool',
        lazy: async () => {
          const { TaskPoolBoard } = await import('@/routes/ai-team/TaskPoolBoard');
          return { Component: TaskPoolBoard };
        },
      },
      {
        // Drag-and-drop agentic workflow studio (BPMN import + DMN decisions +
        // live run visualization) — see routes/process/WorkflowStudioPage.
        path: 'workflows/studio',
        lazy: async () => {
          const { WorkflowStudioPage } = await import('@/routes/process/WorkflowStudioPage');
          return { Component: WorkflowStudioPage };
        },
      },
      {
        path: 'ai-team/control',
        lazy: async () => {
          const { ControlConsolePage } = await import('@/routes/ai-team/ControlConsolePage');
          return { Component: ControlConsolePage };
        },
      },
      {
        path: 'ai-team/audit',
        lazy: async () => {
          const { AuditTrailPage } = await import('@/routes/ai-team/AuditTrailPage');
          return { Component: AuditTrailPage };
        },
      },
      {
        path: 'ai-team/deliverables',
        lazy: async () => {
          const { DeliverablesPage } = await import('@/routes/ai-team/DeliverablesPage');
          return { Component: DeliverablesPage };
        },
      },
      {
        path: 'ai-team/schedules',
        lazy: async () => {
          const { SchedulesPage } = await import('@/routes/ai-team/SchedulesPage');
          return { Component: SchedulesPage };
        },
      },
      // Content + calendar (wave 2)
      {
        path: 'content',
        lazy: async () => {
          const { ContentListPage } = await import('@/routes/content/ContentListPage');
          return { Component: ContentListPage };
        },
      },
      {
        path: 'content/new',
        lazy: async () => {
          const { ContentEditorPage } = await import('@/routes/content/ContentEditorPage');
          return { Component: ContentEditorPage };
        },
      },
      // Carousel slide editor + PNG/PDF export (content-service /carousels/*).
      {
        path: 'content/carousel',
        lazy: async () => {
          const { CarouselEditorPage } = await import('@/routes/content/CarouselEditorPage');
          return { Component: CarouselEditorPage };
        },
      },
      // Native rich-content studio: infographic / GIF+MP4 / post-pack generators.
      {
        path: 'content/studio',
        lazy: async () => {
          const { RichContentStudioPage } = await import('@/routes/content/RichContentStudioPage');
          return { Component: RichContentStudioPage };
        },
      },
      {
        path: 'content/:id',
        lazy: async () => {
          const { ContentEditorPage } = await import('@/routes/content/ContentEditorPage');
          return { Component: ContentEditorPage };
        },
      },
      {
        path: 'calendar',
        lazy: async () => {
          const { CalendarPage } = await import('@/routes/calendar/CalendarPage');
          return { Component: CalendarPage };
        },
      },
      // Platforms + publishing (wave 3)
      {
        path: 'platforms',
        lazy: async () => {
          const { PlatformsPage } = await import('@/routes/platforms/PlatformsPage');
          return { Component: PlatformsPage };
        },
      },
      {
        path: 'publishing',
        lazy: async () => {
          const { PublishingLayout } = await import('@/routes/publishing/PublishingLayout');
          return { Component: PublishingLayout };
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { PublishingPage } = await import('@/routes/publishing/PublishingPage');
              return { Component: PublishingPage };
            },
          },
          {
            path: 'history',
            lazy: async () => {
              const { PublishHistoryPage } = await import('@/routes/publishing/PublishHistoryPage');
              return { Component: PublishHistoryPage };
            },
          },
        ],
      },
      {
        // Evergreen / recycle-queue + content categories + RSS auto-post
        // (self-contained page, its own tabs — kept out of the composer shell).
        path: 'publishing/queue',
        lazy: async () => {
          const { ContentQueuePage } = await import('@/routes/publishing/queue/ContentQueuePage');
          return { Component: ContentQueuePage };
        },
      },
      {
        // Bulk / CSV scheduling — self-contained (own PageHeader), sibling of the
        // composer so it doesn't inherit the Composer/Historique sub-nav.
        path: 'publishing/bulk',
        lazy: async () => {
          const { BulkSchedulePage } = await import('@/routes/publishing/bulk/BulkSchedulePage');
          return { Component: BulkSchedulePage };
        },
      },
      {
        // AI staged post wizard (theme → title → description → detail), self-
        // contained (own PageHeader), sibling of the composer.
        path: 'publishing/wizard',
        lazy: async () => {
          const { AiPostWizard } = await import('@/routes/publishing/AiPostWizard');
          return { Component: AiPostWizard };
        },
      },
      {
        // Post-publication tracking dashboard — picker + per-job detail.
        path: 'publishing/tracking/:jobId?',
        lazy: async () => {
          const { TrackingDashboard } = await import('@/routes/publishing/TrackingDashboard');
          return { Component: TrackingDashboard };
        },
      },
      // Analytics (wave 5)
      {
        path: 'analytics',
        lazy: async () => {
          const { AnalyticsLayout } = await import('@/routes/analytics/AnalyticsLayout');
          return { Component: AnalyticsLayout };
        },
        children: [
          { index: true, element: <Navigate to="/analytics/overview" replace /> },
          {
            path: 'overview',
            lazy: async () => {
              const { AnalyticsOverviewPage } = await import('@/routes/analytics/AnalyticsOverviewPage');
              return { Component: AnalyticsOverviewPage };
            },
          },
          {
            path: 'insights',
            lazy: async () => {
              const { AiInsightsPage } = await import('@/routes/analytics/AiInsightsPage');
              return { Component: AiInsightsPage };
            },
          },
          {
            path: 'hashtags',
            lazy: async () => {
              const { HashtagsPage } = await import('@/routes/analytics/HashtagsPage');
              return { Component: HashtagsPage };
            },
          },
          {
            // Person watchlists / person-metrics (Comptes suivis)
            path: 'people',
            lazy: async () => {
              const { PeopleWatchlistsPage } = await import('@/routes/analytics/PeopleWatchlistsPage');
              return { Component: PeopleWatchlistsPage };
            },
          },
          {
            path: 'competitors',
            lazy: async () => {
              const { CompetitorsPage } = await import('@/routes/analytics/CompetitorsPage');
              return { Component: CompetitorsPage };
            },
          },
          {
            path: 'influencers',
            lazy: async () => {
              const { InfluencersPage } = await import('@/routes/analytics/InfluencersPage');
              return { Component: InfluencersPage };
            },
          },
          {
            path: 'funnels',
            lazy: async () => {
              const { FunnelsPage } = await import('@/routes/analytics/FunnelsPage');
              return { Component: FunnelsPage };
            },
          },
          {
            path: 'cohorts',
            lazy: async () => {
              const { CohortsPage } = await import('@/routes/analytics/CohortsPage');
              return { Component: CohortsPage };
            },
          },
          {
            path: 'attribution',
            lazy: async () => {
              const { AttributionPage } = await import('@/routes/analytics/AttributionPage');
              return { Component: AttributionPage };
            },
          },
          {
            path: 'goals',
            lazy: async () => {
              const { GoalsPage } = await import('@/routes/analytics/GoalsPage');
              return { Component: GoalsPage };
            },
          },
          {
            path: 'reports',
            lazy: async () => {
              const { ReportsPage } = await import('@/routes/analytics/ReportsPage');
              return { Component: ReportsPage };
            },
          },
          {
            path: 'monitoring',
            lazy: async () => {
              const { MonitoringPage } = await import('@/routes/analytics/monitoring/MonitoringPage');
              return { Component: MonitoringPage };
            },
          },
          {
            // Inbox / team performance analytics
            path: 'inbox-performance',
            lazy: async () => {
              const { InboxPerformancePage } = await import('@/routes/analytics/InboxPerformancePage');
              return { Component: InboxPerformancePage };
            },
          },
          {
            path: 'share-of-voice',
            lazy: async () => {
              const { ShareOfVoicePage } = await import('@/routes/analytics/ShareOfVoicePage');
              return { Component: ShareOfVoicePage };
            },
          },
          {
            path: 'vision',
            lazy: async () => {
              const { VisionPage } = await import('@/routes/analytics/VisionPage');
              return { Component: VisionPage };
            },
          },
          {
            path: 'branding',
            lazy: async () => {
              const { BrandingPage } = await import('@/routes/analytics/BrandingPage');
              return { Component: BrandingPage };
            },
          },
          // Intelligence surfaces (analytics-intelligence-service, gateway
          // prefix /api/v1/intelligence) — 18 endpoints previously live but
          // UI-less: saved intelligence dashboards, sentiment, trends, CRM insights.
          {
            path: 'dashboards',
            lazy: async () => {
              const { IntelDashboardsPage } = await import('@/routes/analytics/IntelDashboardsPage');
              return { Component: IntelDashboardsPage };
            },
          },
          {
            path: 'sentiment',
            lazy: async () => {
              const { SentimentPage } = await import('@/routes/analytics/SentimentPage');
              return { Component: SentimentPage };
            },
          },
          {
            path: 'trends',
            lazy: async () => {
              const { TrendsPage } = await import('@/routes/analytics/TrendsPage');
              return { Component: TrendsPage };
            },
          },
          {
            path: 'crm-insights',
            lazy: async () => {
              const { CrmInsightsPage } = await import('@/routes/analytics/CrmInsightsPage');
              return { Component: CrmInsightsPage };
            },
          },
        ],
      },
      // Graphic designer (fabric.js) + workflow builder (React Flow)
      {
        path: 'design',
        lazy: async () => {
          const { DesignGalleryPage } = await import('@/routes/design/DesignGalleryPage');
          return { Component: DesignGalleryPage };
        },
      },
      {
        path: 'design/editor/:id',
        lazy: async () => {
          const { DesignEditorPage } = await import('@/routes/design/DesignEditorPage');
          return { Component: DesignEditorPage };
        },
      },
      {
        path: 'design/brand-kits',
        lazy: async () => {
          const { BrandKitPage } = await import('@/routes/design/BrandKitPage');
          return { Component: BrandKitPage };
        },
      },
      {
        path: 'workflows',
        lazy: async () => {
          const { WorkflowsListPage } = await import('@/routes/workflows/WorkflowsListPage');
          return { Component: WorkflowsListPage };
        },
      },
      {
        path: 'workflows/:id',
        lazy: async () => {
          const { WorkflowBuilderPage } = await import('@/routes/workflows/WorkflowBuilderPage');
          return { Component: WorkflowBuilderPage };
        },
      },
      {
        path: 'media-library',
        lazy: async () => {
          const { MediaLibraryPage } = await import('@/routes/media-library/MediaLibraryPage');
          return { Component: MediaLibraryPage };
        },
      },
      // AI personas / virtual team — activate + launch the ai-orchestrator agents.
      {
        path: 'ai-team',
        lazy: async () => {
          const { AITeamPage } = await import('@/routes/ai-team/AITeamPage');
          return { Component: AITeamPage };
        },
      },
      {
        path: 'ai-assistant',
        lazy: async () => {
          const { AiAssistantPage } = await import('@/routes/ai-assistant/AiAssistantPage');
          return { Component: AiAssistantPage };
        },
      },
      // Services marketplace (gap-fill) — provider directory, bookings & reviews.
      {
        path: 'marketplace',
        lazy: async () => {
          const { MarketplaceLayout } = await import('@/routes/marketplace/MarketplaceLayout');
          return { Component: MarketplaceLayout };
        },
        children: [
          { index: true, element: <Navigate to="/marketplace/browse" replace /> },
          {
            path: 'browse',
            lazy: async () => {
              const { BrowsePage } = await import('@/routes/marketplace/BrowsePage');
              return { Component: BrowsePage };
            },
          },
          {
            // Provider detail as a Modal popup. The URL segment (/marketplace/
            // providers/:id) differs from the browse list, so it can't nest under
            // it; the modal opens over the marketplace shell, closing → browse.
            path: 'providers/:id',
            lazy: async () => {
              const { ProviderDetailPage } = await import('@/routes/marketplace/ProviderDetailPage');
              const { RouteModal } = await import('@/ui/RouteModal');
              return {
                Component: () => (
                  <RouteModal to="/marketplace/browse">
                    <ProviderDetailPage />
                  </RouteModal>
                ),
              };
            },
          },
          {
            path: 'dashboard',
            lazy: async () => {
              const { ProviderDashboardPage } = await import('@/routes/marketplace/ProviderDashboardPage');
              return { Component: ProviderDashboardPage };
            },
          },
          {
            path: 'my-bookings',
            lazy: async () => {
              const { MyBookingsPage } = await import('@/routes/marketplace/MyBookingsPage');
              return { Component: MyBookingsPage };
            },
          },
          {
            path: 'my-reviews',
            lazy: async () => {
              const { MyReviewsPage } = await import('@/routes/marketplace/MyReviewsPage');
              return { Component: MyReviewsPage };
            },
          },
        ],
      },
      // B2B prospection (gap-fill) — searchable directory, lists & enrichment.
      {
        path: 'prospect',
        lazy: async () => {
          const { ProspectLayout } = await import('@/routes/prospect/ProspectLayout');
          return { Component: ProspectLayout };
        },
        children: [
          { index: true, element: <Navigate to="/prospect/search" replace /> },
          {
            path: 'search',
            lazy: async () => {
              const { ProspectSearchPage } = await import('@/routes/prospect/ProspectSearchPage');
              return { Component: ProspectSearchPage };
            },
          },
          {
            path: 'lists',
            lazy: async () => {
              const { ProspectListsPage } = await import('@/routes/prospect/ProspectListsPage');
              return { Component: ProspectListsPage };
            },
          },
        ],
      },
      // Geo-targeted, multi-source commercial discovery (contract:
      // docs/PROSPECTION_CONTRACT.md). Distinct from /prospect (the B2B
      // directory search + build-a-list feature): this hits paid providers
      // (Google Places, OpenCorporates, Crunchbase, Apollo…) live per search.
      {
        path: 'prospection',
        lazy: async () => {
          const { DiscoveryPage } = await import('@/routes/prospection/DiscoveryPage');
          return { Component: DiscoveryPage };
        },
      },
      // Defined prospection STRATEGY — the AI plan that precedes the calendar
      // (/api/v1/prospection/strategy). Step 1 of the funnel; reachable from the
      // step-nav on every prospection page.
      {
        path: 'prospection/strategy',
        lazy: async () => {
          const { StrategyPage } = await import('@/routes/prospection/StrategyPage');
          return { Component: StrategyPage };
        },
      },
      // Rank prospects by goal/product fit (crm /scoring/recommend).
      {
        path: 'prospection/recommend',
        lazy: async () => {
          const { RecommendPage } = await import('@/routes/prospection/RecommendPage');
          return { Component: RecommendPage };
        },
      },
      // Dated multi-touch prospection calendar (/api/v1/prospection/calendar).
      {
        path: 'prospection/calendar',
        lazy: async () => {
          const { ProspectionCalendarPage } = await import('@/routes/prospection/ProspectionCalendarPage');
          return { Component: ProspectionCalendarPage };
        },
      },
      // Per-account AI intelligence dossier (/api/v1/prospection/account-intelligence).
      {
        path: 'prospection/account-intelligence',
        lazy: async () => {
          const { AccountIntelligencePage } = await import('@/routes/prospection/AccountIntelligencePage');
          return { Component: AccountIntelligencePage };
        },
      },
      {
        path: 'crm',
        lazy: async () => {
          const { CrmLayout } = await import('@/routes/crm/CrmLayout');
          return { Component: CrmLayout };
        },
        children: [
          { index: true, element: <Navigate to="/crm/contacts" replace /> },
          {
            path: 'contacts',
            lazy: async () => {
              const { ContactsPage } = await import('@/routes/crm/ContactsPage');
              return { Component: ContactsPage };
            },
          },
          {
            path: 'companies',
            lazy: async () => {
              const { CompaniesPage } = await import('@/routes/crm/CompaniesPage');
              return { Component: CompaniesPage };
            },
          },
          {
            path: 'groups',
            lazy: async () => {
              const { GroupsPage } = await import('@/routes/crm/GroupsPage');
              return { Component: GroupsPage };
            },
          },
          {
            path: 'pipeline',
            lazy: async () => {
              const { PipelinePage } = await import('@/routes/crm/PipelinePage');
              return { Component: PipelinePage };
            },
          },
          {
            path: 'leads',
            lazy: async () => {
              const { LeadsKanbanPage } = await import('@/routes/crm/LeadsKanbanPage');
              return { Component: LeadsKanbanPage };
            },
          },
          {
            path: 'settings',
            lazy: async () => {
              const { CrmSettingsPage } = await import('@/routes/crm/CrmSettingsPage');
              return { Component: CrmSettingsPage };
            },
          },
          {
            path: 'follow-ups',
            lazy: async () => {
              const { FollowUpsPage } = await import('@/routes/crm/FollowUpsPage');
              return { Component: FollowUpsPage };
            },
          },
          {
            path: 'reminders',
            lazy: async () => {
              const { RemindersPage } = await import('@/routes/crm/RemindersPage');
              return { Component: RemindersPage };
            },
          },
          {
            path: 'duplicates',
            lazy: async () => {
              const { DuplicatesPage } = await import('@/routes/crm/DuplicatesPage');
              return { Component: DuplicatesPage };
            },
          },
          {
            path: 'shares',
            lazy: async () => {
              const { SharesPage } = await import('@/routes/crm/SharesPage');
              return { Component: SharesPage };
            },
          },
          {
            path: 'activity',
            lazy: async () => {
              const { ActivityFeedPage } = await import('@/routes/crm/ActivityFeedPage');
              return { Component: ActivityFeedPage };
            },
          },
          {
            path: 'dashboards',
            lazy: async () => {
              const { DashboardsPage } = await import('@/routes/crm/DashboardsPage');
              return { Component: DashboardsPage };
            },
          },
          {
            path: 'scheduler',
            lazy: async () => {
              const { SchedulerPage } = await import('@/routes/crm/SchedulerPage');
              return { Component: SchedulerPage };
            },
          },
          {
            path: 'forms',
            lazy: async () => {
              const { WebFormsPage } = await import('@/routes/crm/WebFormsPage');
              return { Component: WebFormsPage };
            },
          },
          {
            path: 'documents',
            lazy: async () => {
              const { DocumentsPage } = await import('@/routes/crm/DocumentsPage');
              return { Component: DocumentsPage };
            },
          },
          {
            path: 'signals',
            lazy: async () => {
              const { SignalsPage } = await import('@/routes/crm/signals/SignalsPage');
              return { Component: SignalsPage };
            },
          },
        ],
      },
      {
        path: 'social',
        lazy: async () => {
          const { SocialLeadsPage } = await import('@/routes/social/SocialLeadsPage');
          return { Component: SocialLeadsPage };
        },
      },
      {
        path: 'listening',
        lazy: async () => {
          const { ListeningFeedPage } = await import('@/routes/listening/ListeningFeedPage');
          return { Component: ListeningFeedPage };
        },
      },
      {
        path: 'bio',
        lazy: async () => {
          const { BioBuilderPage } = await import('@/routes/bio/BioBuilderPage');
          return { Component: BioBuilderPage };
        },
      },
      {
        path: 'ads',
        lazy: async () => {
          const { AdsPage } = await import('@/routes/ads/AdsPage');
          return { Component: AdsPage };
        },
      },
      {
        path: 'outreach',
        lazy: async () => {
          const { OutreachLayout } = await import('@/routes/outreach/OutreachLayout');
          return { Component: OutreachLayout };
        },
        children: [
          { index: true, element: <Navigate to="campaigns" replace /> },
          {
            path: 'campaigns',
            lazy: async () => {
              const { CampaignsPage } = await import('@/routes/outreach/CampaignsPage');
              return { Component: CampaignsPage };
            },
          },
          {
            path: 'sequences',
            lazy: async () => {
              const { SequencesPage } = await import('@/routes/outreach/SequencesPage');
              return { Component: SequencesPage };
            },
          },
          {
            // Multichannel task queue (LinkedIn/SMS/WhatsApp/call/manual steps)
            path: 'tasks',
            lazy: async () => {
              const { TasksPage } = await import('@/routes/outreach/TasksPage');
              return { Component: TasksPage };
            },
          },
          {
            path: 'templates',
            lazy: async () => {
              const { TemplatesPage } = await import('@/routes/outreach/TemplatesPage');
              return { Component: TemplatesPage };
            },
          },
          {
            path: 'settings',
            lazy: async () => {
              const { OutreachSettingsPage } = await import('@/routes/outreach/OutreachSettingsPage');
              return { Component: OutreachSettingsPage };
            },
          },
          {
            path: 'unibox',
            lazy: async () => {
              const { UniboxPage } = await import('@/routes/outreach/UniboxPage');
              return { Component: UniboxPage };
            },
          },
          {
            path: 'sending',
            lazy: async () => {
              const { SendingPage } = await import('@/routes/outreach/SendingPage');
              return { Component: SendingPage };
            },
          },
        ],
      },
      {
        path: 'inbox',
        lazy: async () => {
          const { InboxPage } = await import('@/routes/inbox/InboxPage');
          return { Component: InboxPage };
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { InboxThreadPage } = await import('@/routes/inbox/InboxThreadPage');
              return { Component: InboxThreadPage };
            },
          },
          {
            path: 'approvals',
            lazy: async () => {
              const { ApprovalsQueuePage } = await import('@/routes/inbox/ApprovalsQueuePage');
              return { Component: ApprovalsQueuePage };
            },
          },
          {
            path: 'automation',
            lazy: async () => {
              const { AutomationPage } = await import('@/routes/inbox/AutomationPage');
              return { Component: AutomationPage };
            },
          },
        ],
      },
      {
        path: 'community',
        lazy: async () => {
          const { CommunityLayout } = await import('@/routes/community/CommunityLayout');
          return { Component: CommunityLayout };
        },
        children: [
          { index: true, element: <Navigate to="/community/canned" replace /> },
          {
            path: 'canned',
            lazy: async () => {
              const { CannedMacrosPage } = await import('@/routes/community/CannedMacrosPage');
              return { Component: CannedMacrosPage };
            },
          },
          {
            path: 'sla',
            lazy: async () => {
              const { SlaPoliciesPage } = await import('@/routes/community/SlaPoliciesPage');
              return { Component: SlaPoliciesPage };
            },
          },
          {
            path: 'csat',
            lazy: async () => {
              const { CsatDashboardPage } = await import('@/routes/community/CsatDashboardPage');
              return { Component: CsatDashboardPage };
            },
          },
          {
            path: 'campaigns',
            lazy: async () => {
              const { InfluencerCampaignsPage } = await import('@/routes/community/InfluencerCampaignsPage');
              return { Component: InfluencerCampaignsPage };
            },
            // Detail nested so the campaigns list stays mounted and the detail
            // opens as a Modal popup on top of it (via <Outlet/> + RouteModal).
            children: [
              {
                path: ':id',
                lazy: async () => {
                  const { InfluencerCampaignDetailPage } = await import(
                    '@/routes/community/InfluencerCampaignDetailPage'
                  );
                  const { RouteModal } = await import('@/ui/RouteModal');
                  return {
                    Component: () => (
                      <RouteModal to="/community/campaigns">
                        <InfluencerCampaignDetailPage />
                      </RouteModal>
                    ),
                  };
                },
              },
            ],
          },
        ],
      },
      {
        path: 'projects',
        lazy: async () => {
          const { ProjectsListPage } = await import('@/routes/projects/ProjectsListPage');
          return { Component: ProjectsListPage };
        },
        // Detail nested → opens as a Modal over the live projects list.
        children: [
          {
            path: ':id',
            lazy: async () => {
              const { ProjectDetailPage } = await import('@/routes/projects/ProjectDetailPage');
              const { RouteModal } = await import('@/ui/RouteModal');
              return {
                Component: () => (
                  <RouteModal to="/projects">
                    <ProjectDetailPage />
                  </RouteModal>
                ),
              };
            },
          },
        ],
      },
      {
        path: 'projects/accept-invite/:token',
        lazy: async () => {
          const { AcceptInvitePage } = await import('@/routes/projects/AcceptInvitePage');
          return { Component: AcceptInvitePage };
        },
      },
      {
        path: 'approvals/*',
        lazy: async () => {
          const { ApprovalsInboxPage } = await import('@/routes/approvals/ApprovalsInboxPage');
          return { Component: ApprovalsInboxPage };
        },
      },
      {
        path: 'staff-questions/*',
        lazy: async () => {
          const { QuestionsInboxPage } = await import('@/routes/staff-questions/QuestionsInboxPage');
          return { Component: QuestionsInboxPage };
        },
      },
      {
        path: 'grants/*',
        lazy: async () => {
          const { GrantRequestsPage } = await import('@/routes/grants/GrantRequestsPage');
          return { Component: GrantRequestsPage };
        },
      },
      {
        path: 'superadmin',
        lazy: async () => {
          const { FleetPage } = await import('@/routes/superadmin/FleetPage');
          return { Component: FleetPage };
        },
      },
      {
        path: 'ai-activity/*',
        lazy: async () => {
          const { AIActivityPage } = await import('@/routes/ai-activity/AIActivityPage');
          return { Component: AIActivityPage };
        },
      },
      // Team orchestration — goal -> multi-persona plan DAG -> live execution
      // (docs/PERSONA_ORCHESTRATION_CONTRACT.md §5/§7). Wires /api/v1/mas/*.
      {
        path: 'orchestration',
        lazy: async () => {
          const { OrchestrationPage } = await import('@/routes/orchestration/OrchestrationPage');
          return { Component: OrchestrationPage };
        },
      },
      // ---------------------------------------------------------------------
      // IA — AI Suite activation pages. Each surfaces a real ai-orchestrator
      // capability (see frontend-spa/src/lib/api/ai-suite.ts). /ai-team is the
      // discovery hub linking to every page below.
      // ---------------------------------------------------------------------
      {
        path: 'ai/content-studio',
        lazy: async () => {
          const { ContentStudioPage } = await import('@/routes/ai/content-studio/ContentStudioPage');
          return { Component: ContentStudioPage };
        },
      },
      {
        path: 'ai/repurpose',
        lazy: async () => {
          const { RepurposePage } = await import('@/routes/ai/repurpose/RepurposePage');
          return { Component: RepurposePage };
        },
      },
      {
        path: 'ai/hook-library',
        lazy: async () => {
          const { HookLibraryPage } = await import('@/routes/ai/hook-library/HookLibraryPage');
          return { Component: HookLibraryPage };
        },
      },
      {
        path: 'ai/brand-voice',
        lazy: async () => {
          const { BrandVoicePage } = await import('@/routes/ai/brand-voice/BrandVoicePage');
          return { Component: BrandVoicePage };
        },
      },
      {
        path: 'ai/ab-copy',
        lazy: async () => {
          const { AbCopyPage } = await import('@/routes/ai/ab-copy/AbCopyPage');
          return { Component: AbCopyPage };
        },
      },
      {
        path: 'ai/suggestions',
        lazy: async () => {
          const { SuggestionsPage } = await import('@/routes/ai/suggestions/SuggestionsPage');
          return { Component: SuggestionsPage };
        },
      },
      {
        path: 'ai/personalization',
        lazy: async () => {
          const { PersonalizationPage } = await import('@/routes/ai/personalization/PersonalizationPage');
          return { Component: PersonalizationPage };
        },
      },
      {
        path: 'ai/community-ai',
        lazy: async () => {
          const { CommunityAiPage } = await import('@/routes/ai/community-ai/CommunityAiPage');
          return { Component: CommunityAiPage };
        },
      },
      {
        path: 'ai/rag-documents',
        lazy: async () => {
          const { RagDocumentsPage } = await import('@/routes/ai/rag-documents/RagDocumentsPage');
          return { Component: RagDocumentsPage };
        },
      },
      {
        path: 'ai/memory',
        lazy: async () => {
          const { MemoryPage } = await import('@/routes/ai/memory/MemoryPage');
          return { Component: MemoryPage };
        },
      },
      {
        path: 'ai/ghostwriter',
        lazy: async () => {
          const { GhostwriterPage } = await import('@/routes/ai/ghostwriter/GhostwriterPage');
          return { Component: GhostwriterPage };
        },
      },
      {
        path: 'settings',
        lazy: async () => {
          const { SettingsLayout } = await import('@/routes/settings/SettingsLayout');
          return { Component: SettingsLayout };
        },
        children: [
          { index: true, element: <Navigate to="credits" replace /> },
          {
            path: 'credits',
            lazy: async () => {
              const { CreditsSettingsPage } = await import('@/routes/settings/CreditsSettingsPage');
              return { Component: CreditsSettingsPage };
            },
          },
          {
            // Alias — "Facturation" surfaces the same credits + purchase page.
            path: 'billing',
            lazy: async () => {
              const { CreditsSettingsPage } = await import('@/routes/settings/CreditsSettingsPage');
              return { Component: CreditsSettingsPage };
            },
          },
          {
            path: 'subscription',
            lazy: async () => {
              const { SubscriptionPage } = await import('@/routes/settings/SubscriptionPage');
              return { Component: SubscriptionPage };
            },
          },
          {
            path: 'referral',
            lazy: async () => {
              const { ReferralPage } = await import('@/routes/settings/ReferralPage');
              return { Component: ReferralPage };
            },
          },
          {
            path: 'ai-registry',
            lazy: async () => {
              const { AIRegistryPage } = await import('@/routes/settings/AIRegistryPage');
              return { Component: AIRegistryPage };
            },
          },
          {
            // Admin model config — allow-in-picker, credit multiplier/cost,
            // and the Auto default-per-task mapping (GET /api/v1/models/config,
            // PATCH /api/v1/models/{id}/picker). Admin-gated inside the page.
            path: 'models',
            lazy: async () => {
              const { ModelsAdminPage } = await import('@/routes/settings/ModelsAdminPage');
              return { Component: ModelsAdminPage };
            },
          },
          {
            path: 'governance',
            lazy: async () => {
              const { GovernancePage } = await import('@/routes/settings/GovernancePage');
              return { Component: GovernancePage };
            },
          },
          {
            // Law-25 / CASL Trust Center — consent ledger, DNC/unsubscribe,
            // regime config, data-subject erasure (compliance-service).
            path: 'trust-center',
            lazy: async () => {
              const { TrustCenterPage } = await import('@/routes/settings/TrustCenterPage');
              return { Component: TrustCenterPage };
            },
          },
          {
            // Organization identity (name/domain/plan/quota) — tenant-service.
            path: 'organization',
            lazy: async () => {
              const { OrganizationSettingsPage } = await import('@/routes/settings/OrganizationSettingsPage');
              return { Component: OrganizationSettingsPage };
            },
          },
          {
            // Notification preferences surfaced in Settings. NotificationPrefs
            // renders as a Modal (needs onClose) → wrap to navigate back on close.
            path: 'notifications',
            lazy: async () => {
              const { NotificationPrefs } = await import('@/routes/notifications/NotificationPrefs');
              const { useNavigate } = await import('react-router');
              const NotificationsSettingsRoute = () => {
                const navigate = useNavigate();
                return <NotificationPrefs onClose={() => navigate('/settings/profile')} />;
              };
              return { Component: NotificationsSettingsRoute };
            },
          },
          {
            // Granular custom roles + resource permission matrix (rbac-service).
            path: 'roles',
            lazy: async () => {
              const { RolesPage } = await import('@/routes/settings/RolesPage');
              return { Component: RolesPage };
            },
          },
          {
            path: 'connectors',
            lazy: async () => {
              const { ConnectorConfigPage } = await import('@/routes/settings/ConnectorConfigPage');
              return { Component: ConnectorConfigPage };
            },
          },
          {
            // Public API / OAuth2 developer-app management
            path: 'developers',
            lazy: async () => {
              const { DeveloperApiPage } = await import('@/routes/settings/DeveloperApiPage');
              return { Component: DeveloperApiPage };
            },
          },
          {
            path: 'integrations',
            lazy: async () => {
              const { IntegrationsHubPage } = await import('@/routes/settings/IntegrationsHubPage');
              return { Component: IntegrationsHubPage };
            },
          },
          {
            path: 'profile',
            lazy: async () => {
              const { ProfileSettingsPage } = await import('@/routes/settings/ProfileSettingsPage');
              return { Component: ProfileSettingsPage };
            },
          },
          {
            path: 'connections',
            lazy: async () => {
              const { ConnectionsSettingsPage } = await import('@/routes/settings/ConnectionsSettingsPage');
              return { Component: ConnectionsSettingsPage };
            },
          },
          // Collaboration (docs/COLLABORATION_CONTRACT.md §1 + §5) — org
          // membership roster, invite/role/remove. Lives under the dedicated
          // routes/team/** feature folder (its role-gating hook is also
          // imported by the CRM share dialog — see routes/crm/shares.tsx).
          {
            path: 'team',
            lazy: async () => {
              const { TeamPage } = await import('@/routes/team/TeamPage');
              return { Component: TeamPage };
            },
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
